import type { ReactNode } from 'react';
import {
  Bell,
  Briefcase,
  Globe,
  Heart,
  Home,
  MessageCircle,
  MessageSquare,
  MoreVertical,
  Repeat2,
  Search,
  Send,
  ThumbsUp,
  Users,
} from 'lucide-react';

import { BrandAvatar } from './BrandAvatar';
import { ExpandableCaption } from './ExpandableCaption';
import { PhoneFrame, StatusBar } from './PhoneFrame';
import { PreviewMedia } from './PreviewMedia';
import { useTranslation } from 'react-i18next';

interface LinkedInPreviewProps {
  caption: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaSlot?: ReactNode;
  displayName: string;
  /** Accepted for uniform prop contract; LinkedIn renders displayName only. */
  handle: string;
  avatarUrl: string | null;
}

const ReactionPill = ({
  icon,
  bg,
  color,
}: {
  icon: React.ReactNode;
  bg: string;
  color: string;
}) => (
  <div
    className="w-[18px] h-[18px] rounded-full border-2 border-white flex items-center justify-center"
    style={{ backgroundColor: bg, color }}
  >
    {icon}
  </div>
);

const FooterAction = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <div className="flex flex-col items-center gap-0.5">
    <div className="text-black/60">{icon}</div>
    <span className="text-[11px] font-semibold text-black/60">{label}</span>
  </div>
);

export const LinkedInPreview = ({
  caption,
  mediaUrl,
  mediaType,
  mediaSlot,
  displayName,
  avatarUrl,
}: LinkedInPreviewProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <PhoneFrame bgcolor="#fff" scrollable>
      <StatusBar tone="dark" />

      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-black/10">
        <BrandAvatar avatarUrl={avatarUrl} size={26} alt={displayName} />
        <div className="flex-1 flex items-center gap-1 px-2 py-1 bg-black/5 rounded-full">
          <Search size={14} color="rgba(0,0,0,0.55)" />
          <span className="text-xs text-black/55">{t('contentTest.linkedInPreview.search')}</span>
        </div>
        <MessageSquare size={20} color="rgba(0,0,0,0.6)" />
      </div>

      <div className="px-3 pt-2 pb-2.5">
        <div className="flex items-start gap-2">
          <BrandAvatar avatarUrl={avatarUrl} size={40} alt={displayName} />
          <div className="flex-1">
            <p className="text-sm font-bold">{displayName}</p>
            <p className="text-xs text-black/55">{t('contentTest.linkedInPreview.promoted')}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-[11px] text-black/55">
                {t('contentTest.linkedInPreview.n1h')}
              </span>
              <Globe size={12} color="rgba(0,0,0,0.55)" />
            </div>
          </div>
          <MoreVertical size={18} color="rgba(0,0,0,0.6)" />
        </div>

        <ExpandableCaption
          data-testid="phone-preview-caption"
          className="text-[13.5px] mt-2.5 leading-[1.4]"
        >
          {caption}
        </ExpandableCaption>

        {mediaSlot ??
          (mediaUrl ? (
            <PreviewMedia
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              className="block w-full mt-3 rounded object-contain"
            />
          ) : null)}

        <div className="flex items-center gap-1 mt-2.5">
          <div className="flex">
            <ReactionPill icon={<ThumbsUp size={10} />} bg="#0a66c2" color="#fff" />
            <div className="-ml-1.5">
              <ReactionPill icon={<Heart size={10} />} bg="#df704d" color="#fff" />
            </div>
          </div>
          <span className="text-xs text-black/60 ml-1">548</span>
          <span className="text-xs text-black/60 ml-auto">
            {t('contentTest.linkedInPreview.n24Comments12Reposts')}
          </span>
        </div>
      </div>

      <div className="flex justify-around py-1.5 border-t border-b border-black/10">
        <FooterAction icon={<ThumbsUp size={18} />} label={t('contentTest.linkedInPreview.like')} />
        <FooterAction
          icon={<MessageCircle size={18} />}
          label={t('contentTest.linkedInPreview.comment')}
        />
        <FooterAction
          icon={<Repeat2 size={18} />}
          label={t('contentTest.linkedInPreview.repost')}
        />
        <FooterAction icon={<Send size={18} />} label={t('contentTest.linkedInPreview.send')} />
      </div>

      <div className="min-h-6 flex-1 bg-[#eeece6]" />

      <div className="flex justify-around py-1.5 border-t border-black/10">
        <FooterAction icon={<Home size={18} />} label={t('contentTest.linkedInPreview.home')} />
        <FooterAction
          icon={<Users size={18} />}
          label={t('contentTest.linkedInPreview.myNetwork')}
        />
        <FooterAction
          icon={<MessageSquare size={18} />}
          label={t('contentTest.linkedInPreview.post')}
        />
        <FooterAction
          icon={<Bell size={18} />}
          label={t('contentTest.linkedInPreview.notifications')}
        />
        <FooterAction
          icon={<Briefcase size={18} />}
          label={t('contentTest.linkedInPreview.jobs')}
        />
      </div>
    </PhoneFrame>
  );
};

LinkedInPreview.displayName = 'LinkedInPreview';
